import React, { useState, Dispatch, SetStateAction } from "react";
import { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { Room, Device } from "generated/graphql";
import { Card } from "collections/Cards";
import { RoomsTable } from "./RoomsTable";
import { DeviceTable } from "./DevicesTable";
import { Layout } from "views/Main/Layouts";
import { useSelector } from "react-redux";
import { State } from "store";
import { Route, Redirect } from "polyrouter";
import SubnetInstructionsModal from "./SubnetInstructionsModal";
import { needs, useGuard } from "rbac";
import "./SiteDetails.scss";

type RoomData = {
  __typename: "Room";
} & Pick<Room, "id" | "name" | "capacity" | "floor"> & {
    devices:
      | ({
          __typename: "Device";
        } & Pick<Device, "id" | "name">)[]
      | null;
  };
type TableOptions = "ROOMS" | "DEVICES";

export const SiteOverviewRouter: React.FC<{
  id: string;
  name: string;
  rooms: Array<RoomData>;
  deviceCount: number;
  fetching: boolean;
  selectedItems: Array<string>;
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
}> = ({
  id,
  name,
  rooms,
  deviceCount,
  fetching,
  selectedItems,
  setSelectedItems
}) => {
  const { t } = useTranslation();
  const { Content } = Layout;
  const [selectedView, setSelectedView] = useState<TableOptions>("ROOMS");
  const subnetInstructions = useSelector((state: State) => {
    return state.userMeta?.subnetInstructions;
  });
  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const count = rooms.length;
  return (
    <Content className="site-overview">
      <Content.Title>
        {t("OVERVIEW")} <p>{`| ${name}`}</p>
      </Content.Title>
      <div className="grouping d-flex clickable">
        <div onClick={() => setSelectedView("ROOMS")} data-testid="room-count">
          <CategoryCard
            title={t("ROOM", {
              count
            })}
            count={count}
            selected={selectedView === "ROOMS"}
          />
        </div>
        <div
          onClick={() => setSelectedView("DEVICES")}
          data-testid="device-count"
        >
          <CategoryCard
            title={t("DEVICE_PLAIN", { count: deviceCount })}
            count={deviceCount}
            selected={selectedView === "DEVICES"}
          />
        </div>
      </div>
      {selectedView === "ROOMS" ? (
        <RoomsTable
          rooms={rooms}
          fetching={fetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          showChecks={userCanEdit}
        />
      ) : (
        <DeviceTable siteId={id} />
      )}
      <Route nest path="instructions">
        <SubnetInstructionsModal />
      </Route>
      {nameIsUnassigned(name) && !subnetInstructions ? (
        <Redirect nest to="instructions" push={true} />
      ) : null}
    </Content>
  );
};

export const CategoryCard: React.FC<{
  title: string;
  count: number;
  selected: boolean;
}> = ({ title, count, selected }) => {
  return (
    <Card className={`mr-1 p-1 text-center ${selected ? `selected` : ""}`}>
      <h1 className="count">{count}</h1>
      <span className="">{title}</span>
    </Card>
  );
};

export default SiteOverviewRouter;
